import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useAccount } from '@/api/useAccount';
import { AgingStrip } from '@/components/invoices/AgingStrip';
import { CustomerFilter } from '@/components/invoices/CustomerFilter';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { formatCurrency, formatDate, formatDateOnly } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  INVOICE_STATUSES,
  InvoiceInvalidError,
  OUTSTANDING,
  useInvoices,
  type InvoiceFilters,
  type InvoiceSortColumn,
  type InvoiceSummary,
} from '@/routes/invoices/useInvoices';
import {
  DEFAULT_INVOICE_FILTERS,
  useInvoiceListFilters,
} from '@/routes/invoices/useInvoiceListFilters';

/**
 * What an invoice's page is handed so its "← All invoices" link returns to
 * the list as it was filtered (Kyle, 2026-09-28): the list's search string.
 */
export interface InvoiceListLinkState {
  listSearch: string;
}

/** Hidden below `md` and restated under the customer's name (M1-22's one-table rule). */
const COMPACT_HIDDEN = 'hidden md:table-cell';

/**
 * How long a sent-date box has to sit still before the list asks with it.
 * A date input reports a value for every digit typed into its year — 0002,
 * 0020, 0202, 2026 — and each would otherwise be a request, and against a
 * Sent from already filled in, a "sentTo cannot be before sentFrom" alert
 * announced mid-typing (review of pass B).
 */
const DATE_SETTLE_MS = 400;

/** The phone's sort select: the column headers that carry the sort are hidden below `md`. */
const SORTABLE: InvoiceSortColumn[] = ['number', 'age', 'total', 'balanceDue'];

/**
 * The invoice list (M2-12, pass B): what the office is owed, and by whom.
 *
 * Opens on Outstanding (Kyle, 2026-09-27), with the total outstanding in the
 * header and the five-bucket aging strip under it. Both are the SERVER's
 * figures, and both follow the customer and sent-date filters only — the
 * status filter and a pressed bucket narrow the rows and leave them standing
 * (pass A's rule; the page never adds anything up).
 *
 * Office only: `customers.read`, as the API gates it. The acts (Send, Void)
 * are on the invoice's own page, like the estimates'.
 */
export function Invoices() {
  const { t } = useTranslation();
  const id = useId();
  const location = useLocation();
  // In the URL (useInvoiceListFilters): Back from an invoice restores them.
  const { filters, setFilters } = useInvoiceListFilters();
  // What the date boxes hold; copied into the URL once it settles.
  const [dates, setDates] = useState({ sentFrom: filters.sentFrom, sentTo: filters.sentTo });
  // The LATEST render's filters and setter, for the settle timer below: a
  // setter captured 400 ms ago merges into the URL as it was then, and would
  // undo a status or sort changed while the date was being typed.
  const latest = useRef({ filters, setFilters });
  latest.current = { filters, setFilters };
  const invoices = useInvoices(filters);
  const account = useAccount();
  const timeZone = account.data?.timezone;

  const set = setFilters;

  useEffect(() => {
    const timer = setTimeout(() => {
      const now = latest.current;
      // Unchanged is no write at all: every write is a history entry.
      if (now.filters.sentFrom !== dates.sentFrom || now.filters.sentTo !== dates.sentTo) {
        now.setFilters(dates);
      }
    }, DATE_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [dates]);

  // The URL's dates changed under the boxes — Back, or a link — so the boxes
  // follow. Keyed on the URL's values alone, so a box being typed in is not
  // reset by some other filter changing meanwhile.
  useEffect(() => {
    setDates((d) =>
      d.sentFrom === filters.sentFrom && d.sentTo === filters.sentTo
        ? d
        : { sentFrom: filters.sentFrom, sentTo: filters.sentTo },
    );
  }, [filters.sentFrom, filters.sentTo]);

  // "Most first" is desc for every column on the API (the newest number, the
  // OLDEST due date, the largest amount), so a newly chosen column starts
  // there and a second press turns it round.
  function toggleSort(column: InvoiceSortColumn) {
    set({
      sortBy: column,
      sortDirection: filters.sortBy === column && filters.sortDirection === 'desc' ? 'asc' : 'desc',
    });
  }

  // A refusal the API explained. A 400 with nothing keyed in it (a date the
  // server could not even bind, which is a plain Bad Request) falls through to
  // the load error below, rather than leaving an empty space where the table was.
  const refusal =
    invoices.error instanceof InvoiceInvalidError &&
    Object.values(invoices.error.errors).flat().length > 0
      ? invoices.error.errors
      : null;
  const sentToProblem = refusal?.sentTo?.[0];
  const otherProblems = refusal
    ? Object.entries(refusal)
        .filter(([key]) => key !== 'sentTo')
        .flatMap(([, sentences]) => sentences)
    : [];
  const filtered =
    filters.status !== DEFAULT_INVOICE_FILTERS.status ||
    filters.customerId !== '' ||
    filters.sentFrom !== '' ||
    filters.sentTo !== '' ||
    filters.aging !== '';
  const linkState: InvoiceListLinkState = { listSearch: location.search };

  // On the SHOP's clock, not the viewer's (web/CONVENTIONS.md, "one clock"):
  // the sent-date filter is the account's calendar, so the date shown beside
  // it has to be too, or an invoice sent at 9pm in Kansas City would read as
  // the next day against a filter that says otherwise.
  const sentOn = (value: string) =>
    formatDate(value, { dateStyle: 'medium', ...(timeZone ? { timeZone } : {}) });

  const list = invoices.data;

  return (
    <section className="space-y-5">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold">{t('invoices.title')}</h1>
        {list ? (
          <p className="text-sm" data-testid="total-outstanding">
            <span className="font-medium">{t('invoices.totalOutstanding')}</span>{' '}
            <span className="text-lg font-semibold tabular-nums">
              {formatCurrency(Number(list.totalOutstanding))}
            </span>{' '}
            <span className="text-[var(--color-muted-foreground)]">
              {t('invoices.outstandingCount', { count: Number(list.outstandingCount) })}
              {' · '}
              {t('invoices.asOf', { date: formatDateOnly(list.asOf) })}
            </span>
          </p>
        ) : null}
      </div>

      <fieldset className="flex flex-wrap items-start gap-3">
        <legend className="sr-only">{t('invoices.filters.legend')}</legend>
        <div className="text-sm">
          <label htmlFor={`${id}-status`} className="mb-1 block font-medium">
            {t('invoices.filters.status')}
          </label>
          <select
            id={`${id}-status`}
            className={FIELD_CLASS}
            value={filters.status}
            onChange={(event) => set({ status: event.target.value })}
          >
            <option value={OUTSTANDING}>{t('invoices.filters.outstanding')}</option>
            <option value="">{t('invoices.filters.allStatuses')}</option>
            {INVOICE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`invoices.status.${s}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="min-w-56 flex-1 sm:max-w-xs">
          <CustomerFilter
            customerId={filters.customerId}
            onChange={(customerId) => set({ customerId })}
          />
        </div>
        <div className="text-sm">
          <label htmlFor={`${id}-sent-from`} className="mb-1 block font-medium">
            {t('invoices.filters.sentFrom')}
          </label>
          <input
            id={`${id}-sent-from`}
            type="date"
            className={FIELD_CLASS}
            value={dates.sentFrom}
            aria-describedby={`${id}-sent-hint`}
            onChange={(event) => setDates((d) => ({ ...d, sentFrom: event.target.value }))}
          />
        </div>
        <div className="text-sm">
          <label htmlFor={`${id}-sent-to`} className="mb-1 block font-medium">
            {t('invoices.filters.sentTo')}
          </label>
          <input
            id={`${id}-sent-to`}
            type="date"
            className={FIELD_CLASS}
            value={dates.sentTo}
            aria-invalid={sentToProblem ? true : undefined}
            aria-describedby={cn(`${id}-sent-hint`, sentToProblem && `${id}-sent-to-error`)}
            onChange={(event) => setDates((d) => ({ ...d, sentTo: event.target.value }))}
          />
          {sentToProblem ? (
            <p
              id={`${id}-sent-to-error`}
              role="alert"
              className="mt-1 text-[var(--color-destructive)]"
            >
              {sentToProblem}
            </p>
          ) : null}
        </div>
        <div className="text-sm md:hidden">
          <label htmlFor={`${id}-sort`} className="mb-1 block font-medium">
            {t('invoices.sort.label')}
          </label>
          <select
            id={`${id}-sort`}
            className={FIELD_CLASS}
            value={`${filters.sortBy}:${filters.sortDirection}`}
            onChange={(event) => {
              const [sortBy, sortDirection] = event.target.value.split(':');
              set({
                sortBy: sortBy as InvoiceSortColumn,
                sortDirection: sortDirection as 'asc' | 'desc',
              });
            }}
          >
            {SORTABLE.flatMap((column) =>
              (['desc', 'asc'] as const).map((direction) => (
                <option key={`${column}:${direction}`} value={`${column}:${direction}`}>
                  {t(`invoices.sort.option.${column}.${direction}`)}
                </option>
              )),
            )}
          </select>
        </div>
        <p id={`${id}-sent-hint`} className="w-full text-xs text-[var(--color-muted-foreground)]">
          {t('invoices.filters.sentHint')}
        </p>
      </fieldset>

      {list ? (
        <AgingStrip
          aging={list.aging}
          selected={filters.aging}
          onSelect={(aging) => set({ aging })}
        />
      ) : null}

      {invoices.isPending ? (
        <LoadingState label={t('invoices.loading')} />
      ) : refusal ? (
        otherProblems.length > 0 ? (
          <div role="alert" className="text-sm text-[var(--color-destructive)]">
            {otherProblems.map((sentence) => (
              <p key={sentence}>{sentence}</p>
            ))}
          </div>
        ) : null
      ) : invoices.isError ? (
        <ErrorState
          title={t('invoices.loadErrorTitle')}
          description={t('invoices.loadErrorDescription')}
          onRetry={() => void invoices.refetch()}
        />
      ) : list!.items.length === 0 ? (
        <EmptyState
          title={
            filtered
              ? t('invoices.emptyFiltered')
              : // The default view is Outstanding, so "nothing here" means
                // nobody owes anything — not that there are no invoices.
                t('invoices.emptyOutstanding')
          }
        />
      ) : (
        <table className="w-full text-sm">
          <caption className="sr-only">{t('invoices.tableCaption')}</caption>
          <thead className="text-left text-[var(--color-muted-foreground)]">
            <tr>
              <SortHeader column="number" filters={filters} onSort={toggleSort}>
                {t('invoices.columns.invoice')}
              </SortHeader>
              <th scope="col" className="py-2 pr-3 font-medium">
                {t('invoices.columns.customer')}
              </th>
              <th scope="col" className={cn('py-2 pr-3 font-medium', COMPACT_HIDDEN)}>
                {t('invoices.columns.status')}
              </th>
              <th scope="col" className={cn('py-2 pr-3 font-medium', COMPACT_HIDDEN)}>
                {t('invoices.columns.sent')}
              </th>
              <SortHeader
                column="age"
                filters={filters}
                onSort={toggleSort}
                className={COMPACT_HIDDEN}
              >
                {t('invoices.columns.due')}
              </SortHeader>
              <SortHeader
                column="total"
                filters={filters}
                onSort={toggleSort}
                className={cn('text-right', COMPACT_HIDDEN)}
              >
                {t('invoices.columns.total')}
              </SortHeader>
              <SortHeader
                column="balanceDue"
                filters={filters}
                onSort={toggleSort}
                className="text-right"
              >
                {t('invoices.columns.balance')}
              </SortHeader>
            </tr>
          </thead>
          <tbody>
            {list!.items.map((invoice) => (
              <InvoiceRow
                key={invoice.id}
                invoice={invoice}
                sentOn={sentOn}
                linkState={linkState}
              />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function SortHeader({
  column,
  filters,
  onSort,
  className,
  children,
}: {
  column: InvoiceSortColumn;
  filters: InvoiceFilters;
  onSort: (column: InvoiceSortColumn) => void;
  className?: string;
  children: string;
}) {
  const { t } = useTranslation();
  const active = filters.sortBy === column;
  return (
    <th
      scope="col"
      className={cn('py-2 pr-3 font-medium', className)}
      // On the th, never the button (axe: aria-allowed-attr), and omitted on
      // unsorted columns — the Jobs list's reasoning (M1-15).
      aria-sort={
        active ? (filters.sortDirection === 'asc' ? 'ascending' : 'descending') : undefined
      }
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className={cn(
          'inline-flex items-center gap-1 hover:underline focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none',
          className?.includes('text-right') && 'flex-row-reverse',
        )}
      >
        {children}
        {active ? (
          <span aria-hidden="true">{filters.sortDirection === 'asc' ? '▲' : '▼'}</span>
        ) : null}
        {active ? (
          <span className="sr-only">
            {t(
              filters.sortDirection === 'asc'
                ? 'invoices.sort.ascending'
                : 'invoices.sort.descending',
            )}
          </span>
        ) : null}
      </button>
    </th>
  );
}

function InvoiceRow({
  invoice,
  sentOn,
  linkState,
}: {
  invoice: InvoiceSummary;
  sentOn: (value: string) => string;
  linkState: InvoiceListLinkState;
}) {
  const { t } = useTranslation();
  const status = t(`invoices.status.${invoice.status}`, { defaultValue: invoice.status });
  const days = invoice.daysOverdue == null ? null : Number(invoice.daysOverdue);
  // A due date is a bare DateOnly: formatted as the day it names (UTC), never
  // through the viewer's zone, which would print the day before west of UTC.
  const due = invoice.dueDate ? formatDateOnly(invoice.dueDate) : null;
  const overdue = days !== null && days > 0 ? t('invoices.daysOverdue', { count: days }) : null;
  // A draft's stored total and balance are its pre-tax subtotal (M2-11 keeps
  // no tax until send), so the list shows neither — the detail page's rule,
  // which a draft's row here used to contradict (review of pass B).
  const amount = (value: number | string) =>
    invoice.status === 'Draft' ? (
      <>
        <span aria-hidden="true">—</span>
        <span className="sr-only">{t('invoices.totals.taxAtSend')}</span>
      </>
    ) : (
      formatCurrency(Number(value))
    );

  return (
    <tr className="border-t border-[var(--color-border)] align-top" data-testid="invoice-row">
      <td className="py-2 pr-3">
        <Link
          to={`/invoices/${invoice.id}`}
          state={linkState}
          className="font-medium hover:underline"
        >
          {t('invoices.numbered', { number: Number(invoice.number) })}
        </Link>
      </td>
      <td className="py-2 pr-3">
        {invoice.customerName}
        {/* The columns hidden below md, restated — one table, so a screen
            reader hears each value once (M1-22). */}
        <span className="block text-xs text-[var(--color-muted-foreground)] md:hidden">
          {[status, due ? t('invoices.dueOn', { date: due }) : null, overdue]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </td>
      <td className={cn('py-2 pr-3', COMPACT_HIDDEN)} data-testid="invoice-status">
        {status}
      </td>
      <td className={cn('py-2 pr-3', COMPACT_HIDDEN)} data-testid="invoice-sent">
        {invoice.sentAt ? sentOn(invoice.sentAt) : '—'}
      </td>
      <td className={cn('py-2 pr-3', COMPACT_HIDDEN)} data-testid="invoice-due">
        {due ?? '—'}
        {overdue ? (
          <span className="block text-xs font-medium text-[var(--color-destructive)]">
            {overdue}
          </span>
        ) : null}
      </td>
      <td className={cn('py-2 pr-3 text-right tabular-nums', COMPACT_HIDDEN)}>
        {amount(invoice.total)}
      </td>
      <td className="py-2 text-right tabular-nums" data-testid="invoice-balance">
        {amount(invoice.balanceDue)}
      </td>
    </tr>
  );
}
